import React, { useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { saveList, deleteList, fetchUserLists } from '../store/listsSlice';
import { mergeTickers, moveTickers, removeTickers } from '../utils/listEdits';

/**
 * Editing the stored lists from the dashboard: merging two lists, and moving,
 * copying or deleting tickers. Everything works on the STORED lists (`lists` is
 * never the type-filtered view), saves through the same endpoints as the list
 * upload, and offers an Undo afterwards.
 *
 * The dialogs it needs are returned as a state object for <ListEditDialogs>; the
 * panel and the matrix's context menu both call the same request* functions.
 *
 * @param {Object}   lists          name -> tickers, as stored
 * @param {Function} onListRemoved  (removedName, replacementName|null) => void, so
 *                                  matrices showing a list that no longer exists can follow it
 */
export default function useListEditing({ lists, onListRemoved }) {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState(null);
  const listsRef = useRef(lists);
  listsRef.current = lists;

  const saveAll = useCallback(async (changes) => {
    for (const [name, tickers] of Object.entries(changes)) {
      await dispatch(saveList({ name, tickers })).unwrap();
    }
  }, [dispatch]);

  const refresh = useCallback(() => dispatch(fetchUserLists()), [dispatch]);

  const offerUndo = useCallback((message, undo) => {
    toast((t) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
        <span>{message}</span>
        <button
          type="button"
          style={{ cursor: 'pointer', fontWeight: 700, border: 'none', background: 'transparent', color: '#1976d2' }}
          onClick={async () => {
            toast.dismiss(t.id);
            try { await undo(); await refresh(); toast.success('Undone'); } catch (e) { toast.error(`Undo failed: ${e}`); }
          }}
        >
          Undo
        </button>
      </span>
    ), { duration: 8000 });
  }, [refresh]);

  const fail = (what, e) => toast.error(`${what} failed: ${typeof e === 'string' ? e : (e?.message || e)}`);

  /** Move (or copy) tickers between two lists. */
  const applyMove = useCallback(async (from, to, tickers, copy = false, { confirmedEmpty = false } = {}) => {
    const current = listsRef.current;
    if (!tickers.length || (!copy && from === to)) return;
    const { changes, emptied } = moveTickers(current, from, to, tickers, copy);
    if (emptied && !confirmedEmpty) {
      setDialog({ kind: 'emptied', from, to, tickers, copy });
      return;
    }
    const before = Object.fromEntries(Object.keys(changes).map(n => [n, current[n]]));
    try {
      await saveAll(changes);
      if (emptied) {
        const original = current[from];
        await dispatch(deleteList(from)).unwrap();
        onListRemoved && onListRemoved(from, to);
        offerUndo(to
          ? `Moved ${tickers.length} to '${to}'; '${from}' was empty and is deleted`
          : `Removed ${tickers.length} from '${from}'; it was empty and is deleted`, async () => {
          await saveAll({ ...before, [from]: original });
        });
      } else {
        const verb = copy ? 'Copied' : (to ? 'Moved' : 'Removed');
        offerUndo(to ? `${verb} ${tickers.length} to '${to}'` : `${verb} ${tickers.length} from '${from}'`, async () => {
          await saveAll(before);
        });
      }
      await refresh();
    } catch (e) {
      fail('Saving the lists', e);
      await refresh();
    }
  }, [saveAll, dispatch, onListRemoved, offerUndo, refresh]);

  const requestDeleteTickers = useCallback(async (from, tickers) => {
    const current = listsRef.current;
    const { changes, emptied } = removeTickers(current, from, tickers);
    if (emptied) {
      setDialog({ kind: 'emptied', from, to: null, tickers, copy: false });
      return;
    }
    const before = { [from]: current[from] };
    try {
      await saveAll(changes);
      offerUndo(`Removed ${tickers.length} from '${from}'`, async () => { await saveAll(before); });
      await refresh();
    } catch (e) {
      fail('Saving the list', e);
      await refresh();
    }
  }, [saveAll, offerUndo, refresh]);

  const requestMoveTickers = useCallback((from, tickers) => {
    setDialog({ kind: 'pick', from, tickers });
  }, []);

  const requestMerge = useCallback((source, target) => {
    if (!source || !target || source === target) return;
    setDialog({ kind: 'merge', source, target });
  }, []);

  const confirmMerge = useCallback(async (source, target, deleteSource) => {
    const current = listsRef.current;
    const merged = mergeTickers(current[target] || [], current[source] || []);
    const before = { [target]: current[target] };
    const sourceBefore = current[source];
    try {
      await saveAll({ [target]: merged });
      if (deleteSource) {
        await dispatch(deleteList(source)).unwrap();
        onListRemoved && onListRemoved(source, target);
      }
      offerUndo(`Merged '${source}' into '${target}' (${merged.length} tickers)`, async () => {
        await saveAll(deleteSource ? { ...before, [source]: sourceBefore } : before);
      });
      await refresh();
    } catch (e) {
      fail('Merging', e);
      await refresh();
    }
  }, [saveAll, dispatch, onListRemoved, offerUndo, refresh]);

  const closeDialog = useCallback(() => setDialog(null), []);

  return {
    dialog,
    closeDialog,
    requestMerge,
    confirmMerge,
    requestMoveTickers,
    requestDeleteTickers,
    applyMove,
  };
}
